/** @jsx isolateComponent */
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';
import { css } from '@emotion/react';

import {
  BrandButton,
  Button,
  ButtonProps,
  Colors,
  DarkBackgroundButton,
  Heading,
  Text,
  Typography,
  Icon,
  TagLabel,
  Grid,
} from '../../../../index';
import { SubscriptionFormWithDisclaimer } from '../../../forms/SubscriptionForm';
import { createUniqueCustomProperties } from '../../../../utils/createUniqueCustomProperty';
import { HeroColorsV4, HeroColorsV5 } from '../types';
import {
  transformBackgroundColor,
  validateZapierBackgroundAccessibility,
  getSupportedCTAColor,
} from '../utils';
import { breakpoints } from '../constants';
import type { AnchorTarget } from '../../../../ts-types';
import type { IconName } from '../../../display/Icon';

export type Props = {
  /**
   * Controls the background of the entire element.
   *
   * Must be one of type `'lava60' | 'ocean40' | 'sun60' | 'white' | 'zapier' | 'night' | 'moss' | 'neutral800'`.
   * */
  backgroundColor?: HeroColorsV4 | Exclude<HeroColorsV5, 'neutral100'>;
  /**
   * Heading text content.
   */
  heading: React.ReactNode;
  /**
   * Text content displayed below the heading.
   */
  description?: React.ReactNode;
  /**
   * Whether the subscription form is enabled in place of CTA button.
   */
  isSubscriptionForm?: boolean;
  /**
   * Placeholder text to display inside the subscription form input field.
   */
  subscriptionPlaceholder?: string;
  /**
   * Background color of the CTA button.
   */
  ctaColor?: 'primary-alt' | 'primary-dark' | 'brand';
  /**
   * Text content of the CTA button.
   */
  ctaText?: React.ReactNode;
  /**
   * href for the CTA button.
   */
  ctaHref?: string;
  /**
   * Text displayed next to CTA button.
   */
  ctaDisclaimer?: React.ReactNode;
  /**
   * CTA button click handler.
   */
  onCtaClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  /**
   * CTA button keyDown handler.
   */
  onCtaKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  /**
   * Renders in the space opposite the text.
   * Can be used to render media such as images, videos, or any other React Component. */
  children?: React.ReactNode;
  /**
   * Function that gets called on subscription form submit.
   */
  onSubscriptionFormSubmit?: (subscriptionValue?: string) => void;
  /**
   * The `target` attribute for the CTA button.
   */
  ctaTarget?: AnchorTarget;
  /**
   * A label that renders as a small tag
   * with a background color and an icon
   */
  label?: string;
  /**
   * The icon that is displayed in the label.
   */
  labelIcon?: IconName;
  /**
   * The labelColor determines the background-color of the label.
   */
  labelColor?:
    | 'info'
    | 'success'
    | 'error'
    | 'warning'
    | 'white'
    | 'gray'
    | 'peach'
    | 'neutral300'
    | 'bolt'
    | 'cream';
};

// Shared CSS variables used amongst `HeroColorBlock`.
const Vars = createUniqueCustomProperties('HeroColorBlock', [
  'primaryBackgroundColorStop',
  'secondaryBackgroundColorStop',
]);

const SubscriptionFormStyles = {
  ctaContainer: css`
    max-width: 470px;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      align-items: stretch;
      flex-direction: column;
    }
  `,
  ctaDisclaimer: css`
    margin: 10px 0;
    text-align: left;
  `,
};

const Styles = labelStyles('HeroColorBlock', {
  root: (backgroundColor: Props['backgroundColor'] = 'zapier') => css`
    padding: 20px 19.5px;
    ${Vars.primaryBackgroundColorStop}: ${Colors[backgroundColor]};
    ${Vars.secondaryBackgroundColorStop}: ${Colors.neutral100};
    background: linear-gradient(
      to bottom,
      var(${Vars.primaryBackgroundColorStop}) 80%,
      var(${Vars.secondaryBackgroundColorStop}) 20%
    );
    @media screen and (min-width: ${breakpoints.mobile}px) {
      padding: 40px 31px;
      background: linear-gradient(
        to bottom,
        var(${Vars.primaryBackgroundColorStop}) 75%,
        var(${Vars.secondaryBackgroundColorStop}) 25%
      );
    }
    @media screen and (min-width: ${breakpoints.tablet}px) {
      padding: 60px 31px;
      background: linear-gradient(
        to bottom,
        var(${Vars.primaryBackgroundColorStop}) 54.55%,
        var(${Vars.secondaryBackgroundColorStop}) 45.45%
      );
    }
  `,
  content: css`
    max-width: 770px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
  `,
  textContent: css`
    margin: auto;
  `,
  labelContainer: css`
    margin-bottom: 10px;
  `,
  heading: (backgroundColor: Props['backgroundColor']) => css`
    ${Typography.pageHeader4}
    color: ${Colors[backgroundColor === 'zapier' ? 'cream' : 'neutral100']};
    margin-bottom: 10px;
    text-align: center;

    @media screen and (min-width: ${breakpoints.tablet}px) {
      ${Typography.pageHeader3}
      margin-bottom: 20px;
    }
  `,
  description: (backgroundColor: Props['backgroundColor']) => css`
    ${Typography.pageHeader9}
    color: ${Colors[backgroundColor === 'zapier' ? 'cream' : 'neutral100']};
    text-align: center;
    margin: 0 auto;
    max-width: 570px;
  `,
  ctaContainer: (
    isSubscriptionForm: Props['isSubscriptionForm'] = false
  ) => css`
    margin-top: 20px;
    text-align: center;
    width: 100%;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      margin-top: 40px;
    }
    ${isSubscriptionForm &&
    withModifier('subscription-form', SubscriptionFormStyles.ctaContainer)}
  `,
  ctaButtonWrapper: css`
    margin: 0 auto;

    // Button should take up full width in mobile only;
    button,
    a {
      width: 100%;
    }

    @media screen and (min-width: ${breakpoints.tablet}px) {
      button,
      a {
        width: auto;
      }
    }
  `,
  ctaDisclaimer: (
    isSubscriptionForm: Props['isSubscriptionForm'] = false
  ) => css`
    margin: 10px 0 0 0;
    text-align: center;
    ${isSubscriptionForm &&
    withModifier(
      'subscription-form-disclaimer',
      SubscriptionFormStyles.ctaDisclaimer
    )}
  `,
  mediaContent: css`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 40px;
  `,
});

export const HeroColorBlock = ({
  ctaColor = 'primary-alt',
  isSubscriptionForm = false,
  subscriptionPlaceholder = 'Email address',
  backgroundColor = 'zapier',
  heading,
  description,
  ctaText,
  ctaHref,
  ctaDisclaimer,
  onCtaClick,
  onCtaKeyDown,
  children,
  onSubscriptionFormSubmit,
  ctaTarget,
  label,
  labelIcon,
  labelColor,
}: Props) => {
  const mappedBackgroundColor = transformBackgroundColor(backgroundColor);

  // This should validate and through a console error as the disclaimer text accessibility
  // is poor on the zapier background.
  validateZapierBackgroundAccessibility(
    mappedBackgroundColor,
    isSubscriptionForm,
    !!ctaDisclaimer
  );

  const getCtaComponent = () => {
    const sharedProps = {
      children: ctaText,
      href: !isSubscriptionForm ? ctaHref : undefined,
      onClick: onCtaClick,
      onKeyDown: onCtaKeyDown,
      type: isSubscriptionForm
        ? ('submit' as ButtonProps['type'])
        : ('button' as ButtonProps['type']),
      'data-testid': 'color-block-hero-cta-button',
      target: ctaTarget,
    };

    const color = getSupportedCTAColor(ctaColor, mappedBackgroundColor);

    return color === 'brand' ? (
      <BrandButton {...sharedProps} />
    ) : color === 'primary-dark' ? (
      <DarkBackgroundButton {...sharedProps} />
    ) : (
      <Button {...sharedProps} color={color} size="small" />
    );
  };

  return (
    <div
      css={Styles.root(mappedBackgroundColor)}
      data-background-color={mappedBackgroundColor}
    >
      <div css={Styles.content}>
        {label && (
          <div css={Styles.labelContainer}>
            <TagLabel color={labelColor}>
              {labelIcon ? (
                <Grid alignItems="center" columns="auto max-content" gap={5}>
                  <Icon name={labelIcon} size={20} />
                  {label}
                </Grid>
              ) : (
                label
              )}
            </TagLabel>
          </div>
        )}
        <Heading css={Styles.heading(mappedBackgroundColor)}>{heading}</Heading>
        {description && (
          <div css={Styles.description(mappedBackgroundColor)}>
            {description}
          </div>
        )}
        {ctaText && (ctaHref || onCtaClick || isSubscriptionForm) && (
          <div css={Styles.ctaContainer(isSubscriptionForm)}>
            {isSubscriptionForm ? (
              <SubscriptionFormWithDisclaimer
                placeholderText={subscriptionPlaceholder}
                onSubmit={onSubscriptionFormSubmit}
                renderButton={() => getCtaComponent()}
                disclaimerTextColor={
                  mappedBackgroundColor === 'neutral100'
                    ? 'primary'
                    : 'secondary'
                }
              />
            ) : (
              <div>
                <div css={Styles.ctaButtonWrapper}>{getCtaComponent()}</div>
                {ctaDisclaimer && (
                  <div css={Styles.ctaDisclaimer(isSubscriptionForm)}>
                    <Text
                      tag="div"
                      type="paragraph3"
                      color={
                        mappedBackgroundColor === 'zapier'
                          ? 'cream'
                          : 'neutral100'
                      }
                    >
                      {ctaDisclaimer}
                    </Text>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <div css={Styles.mediaContent}>{children}</div>
      </div>
    </div>
  );
};
